import React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
import { reduxForm, InjectedFormProps } from 'redux-form'
// components
import { ReduxInput } from 'components/FormElements/ReduxForm'
import { Button, BUTTON_LAYOUTS, CREATE_BUTTON_STYLE } from 'components/UI'
// graphql
import { AUTH_TWO_FACTOR_CONFIRM_SETUP_MUTATION } from 'graphql/auth/twoFactor/mutations'
// constants
import { Forms } from 'constants/forms'
// views
import { StepTitle, ButtonGroup, ConfirmForm } from '../views'
// utils
import { required } from 'utils/validators'
import { handleError } from 'utils/errors/handlers'

interface Props {
  setupToken?: string | null
  onSuccess: () => void
  onBack: () => void
}

type FormData = {
  code: string
}

const ConfirmStep: React.FC<InjectedFormProps<FormData, Props> & Props> = ({
  handleSubmit,
  valid,
  setupToken,
  onSuccess,
  onBack
}) => {
  const mutationContext = setupToken ? { headers: { Authorization: `Bearer ${setupToken}` } } : undefined

  const [confirmMutation, { loading }] = useMutation(AUTH_TWO_FACTOR_CONFIRM_SETUP_MUTATION)

  const handleConfirm = async (formData: FormData) => {
    try {
      await confirmMutation({
        variables: { code: formData.code.trim() },
        context: mutationContext
      })

      onSuccess()
    } catch (error) {
      handleError(error, 'TwoFactorSetupModal.handleConfirm')
    }
  }

  return (
    <ConfirmForm onSubmit={handleSubmit(handleConfirm)}>
      <StepTitle>Step 3: Verify Your Authenticator</StepTitle>
      <p>Enter the 6-digit code from your authenticator app to confirm setup.</p>

      <ReduxInput
        autoFocus
        type='text'
        name='code'
        autoComplete='off'
        validate={[required]}
        label='Verification Code'
      />

      <ButtonGroup>
        <Button layout={BUTTON_LAYOUTS.WHITE} onClick={onBack} type='button'>
          Back
        </Button>

        <Button layout={BUTTON_LAYOUTS.PURPLE} type='submit' disabled={!valid || loading} style={CREATE_BUTTON_STYLE}>
          Complete Setup
        </Button>
      </ButtonGroup>
    </ConfirmForm>
  )
}

export default reduxForm<FormData, Props>({
  form: Forms.TWO_FACTOR_CONFIRM_SETUP_FORM
})(ConfirmStep)
